// Enhanced encryption service with local key storage
import CryptoJS from "crypto-js";
import { auth } from "../config/firebase"; // Import Firebase auth
import { getStorage, ref as storageRef, getBlob } from "firebase/storage";

class LocalEncryptionService {
  constructor() {
    this.sessionKeys = new Map(); // In-memory storage for active session keys
    this.keyDerivationIterations = 100000; // PBKDF2 iterations
    this.currentUser = null; // Cache current user
  }

  /**
   * Get the current user's ID from Firebase Auth
   * @returns {string|null} - Current user's ID or null if not authenticated
   */
  getCurrentUserId() {
    try {
      // Try to get from cached user first
      if (this.currentUser?.uid) {
        return this.currentUser.uid;
      }

      // Get from Firebase auth
      const user = auth.currentUser;
      if (user?.uid) {
        this.currentUser = user;
        return user.uid;
      }

      return null;
    } catch (error) {
      console.error("Error getting current user ID:", error);
      return null;
    }
  }

  /**
   * Initialize encryption keys for a user
   * @param {string} userId - User's ID
   * @param {string} userPassword - User's password for key derivation
   * @returns {Promise<boolean>} - Success status
   */
  async initializeUserKeys(userId, userPassword) {
    try {
      if (!userId || !userPassword) {
        console.error("Missing userId or userPassword for key initialization");
        return false;
      }

      // Set current user
      this.currentUser = { uid: userId };

      // Generate or retrieve master key
      const masterKey = await this.generateMasterKey(userId, userPassword);

      if (masterKey) {
        console.log(
          "Encryption keys initialized successfully for user:",
          userId
        );
        return true;
      }

      return false;
    } catch (error) {
      console.error("Error initializing user keys:", error);
      return false;
    }
  }

  /**
   * Generate a master encryption key for the user (stored locally)
   * @param {string} userId - User's ID
   * @param {string} userPassword - User's password (for key derivation)
   * @returns {Promise<string>} - Master key
   */
  async generateMasterKey(userId, userPassword) {
    try {
      // Use PBKDF2 for key derivation from password
      const salt = CryptoJS.lib.WordArray.random(256 / 8);
      const masterKey = CryptoJS.PBKDF2(userPassword, salt, {
        keySize: 256 / 32,
        iterations: this.keyDerivationIterations,
      });

      // Store salt and derived key locally (encrypted)
      const encryptedData = {
        salt: salt.toString(),
        masterKey: masterKey.toString(),
        timestamp: Date.now(),
      };

      localStorage.setItem(
        `mnop_master_key_${userId}`,
        JSON.stringify(encryptedData)
      );

      return masterKey.toString();
    } catch (error) {
      console.error("Error generating master key:", error);
      throw error;
    }
  }

  /**
   * Derive a conversation key from the master key (not stored anywhere)
   * @param {string} userId - Current user's ID
   * @param {string} otherUserId - Other participant's ID
   * @param {string} masterKey - User's master key
   * @returns {string} - Conversation-specific key
   */
  deriveConversationKey(userId, otherUserId, masterKey) {
    // Sort IDs to ensure same key generation regardless of order
    const sortedIds = [userId, otherUserId].sort().join("_");

    // Derive conversation key from master key + conversation identifier
    const conversationKey = CryptoJS.HmacSHA256(sortedIds, masterKey);

    return conversationKey.toString();
  }

  /**
   * Generate a temporary session key for a conversation
   * @param {string} userId - Current user's ID
   * @param {string} otherUserId - Other participant's ID
   * @returns {string} - Session key
   */
  generateSessionKey(userId, otherUserId) {
    const conversationId = [userId, otherUserId].sort().join("_");

    // Check if we already have a session key for this conversation
    if (this.sessionKeys.has(conversationId)) {
      return this.sessionKeys.get(conversationId);
    }

    // Generate new session key
    const sessionKey = CryptoJS.lib.WordArray.random(256 / 8).toString();

    // Store in memory for the session
    this.sessionKeys.set(conversationId, sessionKey);

    // Auto-expire session keys after 1 hour
    setTimeout(() => {
      this.sessionKeys.delete(conversationId);
    }, 60 * 60 * 1000);

    return sessionKey;
  }

  /**
   * Get conversation encryption key (tries session key first, falls back to derived key)
   * @param {string} userId - Current user's ID
   * @param {string} otherUserId - Other participant's ID
   * @param {string} userPassword - User's password (for master key derivation)
   * @returns {Promise<string>} - Encryption key for the conversation
   */
  async getConversationKey(userId, otherUserId, userPassword = null) {
    try {
      const conversationId = [userId, otherUserId].sort().join("_");

      // First try to get session key
      if (this.sessionKeys.has(conversationId)) {
        return this.sessionKeys.get(conversationId);
      }

      // If no session key, derive from master key
      if (userPassword) {
        const masterKey = await this.getMasterKey(userId, userPassword);
        const conversationKey = this.deriveConversationKey(
          userId,
          otherUserId,
          masterKey
        );

        // Store as session key for future use
        this.sessionKeys.set(conversationId, conversationKey);

        return conversationKey;
      }

      // Fallback to deterministic generation (less secure)
      console.warn(
        "Using fallback key generation - consider providing user password"
      );
      return this.generateDeterministicKey(userId, otherUserId);
    } catch (error) {
      console.error("Error getting conversation key:", error);
      throw error;
    }
  }

  /**
   * Get master key from local storage
   * @param {string} userId - User's ID
   * @param {string} userPassword - User's password
   * @returns {Promise<string>} - Master key
   */
  async getMasterKey(userId, userPassword) {
    try {
      const stored = localStorage.getItem(`mnop_master_key_${userId}`);

      if (!stored) {
        // Generate new master key if none exists
        return await this.generateMasterKey(userId, userPassword);
      }

      const encryptedData = JSON.parse(stored);

      // Re-derive the master key using stored salt
      const salt = CryptoJS.enc.Hex.parse(encryptedData.salt);
      const masterKey = CryptoJS.PBKDF2(userPassword, salt, {
        keySize: 256 / 32,
        iterations: this.keyDerivationIterations,
      });

      return masterKey.toString();
    } catch (error) {
      console.error("Error retrieving master key:", error);
      throw error;
    }
  }

  /**
   * Fallback deterministic key generation (for backward compatibility)
   * @param {string} user1Id - First user's ID
   * @param {string} user2Id - Second user's ID
   * @returns {string} - Deterministic key
   */
  generateDeterministicKey(user1Id, user2Id) {
    const sortedIds = [user1Id, user2Id].sort().join("_");
    return CryptoJS.SHA256(sortedIds).toString();
  }

  /**
   * Encrypt a message using AES encryption
   * @param {string} message - The plain text message to encrypt
   * @param {string} encryptionKey - The key to use for encryption
   * @returns {string} - The encrypted message
   */
  encryptMessage(message, encryptionKey) {
    if (!message || !encryptionKey) {
      console.error("Missing required parameters for encryption");
      return null;
    }

    return CryptoJS.AES.encrypt(message, encryptionKey).toString();
  }

  /**
   * Decrypt a message using AES decryption
   * @param {string} encryptedMessage - The encrypted message
   * @param {string} encryptionKey - The key to use for decryption
   * @returns {string} - The decrypted message
   */
  decryptMessage(encryptedMessage, encryptionKey) {
    if (!encryptedMessage || !encryptionKey) {
      console.error("Missing required parameters for decryption");
      return null;
    }

    try {
      const bytes = CryptoJS.AES.decrypt(encryptedMessage, encryptionKey);
      return bytes.toString(CryptoJS.enc.Utf8);
    } catch (error) {
      console.error("Failed to decrypt message:", error);
      return "[Encrypted message - unable to decrypt]";
    }
  }

  /**
   * Encrypt a file using AES encryption
   * @param {File|Blob} file - The file to encrypt
   * @param {string} encryptionKey - The key to use for encryption
   * @returns {Promise<{encryptedFile: Blob, metadata: Object}>} - The encrypted file and its metadata
   */
  async encryptFile(file, encryptionKey) {
    if (!file || !encryptionKey) {
      console.error("Missing required parameters for file encryption");
      return null;
    }

    try {
      const arrayBuffer = await file.arrayBuffer();
      const wordArray = CryptoJS.lib.WordArray.create(arrayBuffer);
      const encryptedContent = CryptoJS.AES.encrypt(
        wordArray,
        encryptionKey
      ).toString();

      const encryptedBlob = new Blob([encryptedContent], {
        type: "application/encrypted",
      });

      return {
        encryptedFile: encryptedBlob,
        metadata: {
          originalType: file.type,
          originalSize: file.size,
          originalName: file.name,
          encrypted: true,
        },
      };
    } catch (error) {
      console.error("Failed to encrypt file:", error);
      return null;
    }
  }

  /**
   * Decrypt a file using AES decryption
   * @param {Blob} encryptedBlob - The encrypted file blob
   * @param {string} encryptionKey - The key to use for decryption
   * @param {Object} metadata - Original file metadata including type
   * @returns {Promise<Blob>} - The decrypted file blob
   */
  async decryptFile(encryptedBlob, encryptionKey, metadata) {
    if (!encryptedBlob || !encryptionKey || !metadata) {
      console.error("Missing required parameters for file decryption");
      return null;
    }

    try {
      const encryptedContent = await encryptedBlob.text();
      const decrypted = CryptoJS.AES.decrypt(encryptedContent, encryptionKey);

      // Convert WordArray to ArrayBuffer
      const arrayBuffer = this.wordArrayToArrayBuffer(decrypted);

      return new Blob([arrayBuffer], { type: metadata.originalType });
    } catch (error) {
      console.error("Failed to decrypt file:", error);
      return null;
    }
  }

  /**
   * Create an object URL for an encrypted file
   * @param {string} downloadURL - URL to the encrypted file
   * @param {string} encryptionKey - The key to use for decryption
   * @param {Object} metadata - Original file metadata
   * @returns {Promise<string>} - Object URL for the decrypted file
   */
  async createDecryptedObjectURL(downloadURL, encryptionKey, metadata) {
    try {
      let encryptedBlob;
      
      // Try to extract storage path from URL and use Firebase SDK
      try {
        // Parse the Firebase Storage URL to get the path
        const url = new URL(downloadURL);
        const pathMatch = url.pathname.match(/\/o\/(.+?)(\?|$)/);
        
        if (pathMatch) {
          // Decode the path (Firebase encodes it)
          const storagePath = decodeURIComponent(pathMatch[1]);
          const storage = getStorage();
          const fileRef = storageRef(storage, storagePath);
          
          // Use Firebase SDK's getBlob which handles auth and CORS
          encryptedBlob = await getBlob(fileRef);
        } else {
          // Fallback to fetch if URL parsing fails
          const response = await fetch(downloadURL);
          if (!response.ok) {
            throw new Error(`Failed to fetch file: ${response.status}`);
          }
          encryptedBlob = await response.blob();
        }
      } catch (parseError) {
        // If Firebase SDK approach fails, try direct fetch as fallback
        console.warn('Firebase SDK fetch failed, trying direct fetch:', parseError);
        const response = await fetch(downloadURL);
        if (!response.ok) {
          throw new Error(`Failed to fetch file: ${response.status}`);
        }
        encryptedBlob = await response.blob();
      }

      const decryptedBlob = await this.decryptFile(
        encryptedBlob,
        encryptionKey,
        metadata
      );

      if (!decryptedBlob) {
        throw new Error("Failed to decrypt file");
      }

      return URL.createObjectURL(decryptedBlob);
    } catch (error) {
      console.error("Error creating decrypted object URL:", error);

      // CORS fallback for development
      if (
        error.message?.includes("CORS") ||
        process.env.NODE_ENV === "development"
      ) {
        const fallbackText = `Encrypted File: ${
          metadata?.originalName || "file"
        }\nCannot load due to CORS restrictions in development mode.`;
        const fallbackBlob = new Blob([fallbackText], { type: "text/plain" });
        return URL.createObjectURL(fallbackBlob);
      }

      throw error;
    }
  }

  /**
   * Helper function to convert CryptoJS WordArray to ArrayBuffer
   * @param {WordArray} wordArray - CryptoJS WordArray
   * @returns {ArrayBuffer} - ArrayBuffer
   */
  wordArrayToArrayBuffer(wordArray) {
    const words = wordArray.words;
    const sigBytes = wordArray.sigBytes;
    const buffer = new ArrayBuffer(sigBytes);
    const view = new Uint8Array(buffer);

    for (let i = 0; i < sigBytes; i++) {
      const byteIndex = Math.floor(i / 4);
      const byteOffset = i % 4;
      const wordValue = words[byteIndex];
      view[i] = (wordValue >> (24 - 8 * byteOffset)) & 0xff;
    }

    return buffer;
  }

  /**
   * Clear all local keys and session data
   * @param {string} userId - User ID to clear keys for
   */
  clearUserKeys(userId) {
    // Clear master key from localStorage
    localStorage.removeItem(`mnop_master_key_${userId}`);

    // Clear session keys involving this user
    for (const [conversationId, key] of this.sessionKeys.entries()) {
      if (conversationId.includes(userId)) {
        this.sessionKeys.delete(conversationId);
      }
    }
  }

  /**
   * Clear all session keys (call on logout)
   */
  clearAllSessionKeys() {
    this.sessionKeys.clear();
  }

  /**
   * Check if the encryption service is initialized for the current user
   * @returns {Promise<boolean>} - Whether the service is initialized
   */
  async isInitialized() {
    try {
      const userId = this.getCurrentUserId();
      if (!userId) return false;

      const storedData = localStorage.getItem(`mnop_master_key_${userId}`);
      return !!storedData;
    } catch (error) {
      console.error("Error checking initialization status:", error);
      return false;
    }
  }

  /**
   * Clear all encryption keys and data for the current user
   * @returns {Promise<void>}
   */
  async clearKeys() {
    try {
      const userId = this.getCurrentUserId();
      if (!userId) return;

      // Clear master key from localStorage
      localStorage.removeItem(`mnop_master_key_${userId}`);

      // Clear any session keys from secure storage
      const keys = await this.secureStorage.getAllKeys();
      for (const key of keys) {
        if (key.startsWith(`session_key_${userId}_`)) {
          await this.secureStorage.remove(key);
        }
      }

      console.log("Encryption keys cleared for user:", userId);
    } catch (error) {
      console.error("Error clearing encryption keys:", error);
      throw error;
    }
  }
}

export default new LocalEncryptionService();
